import {
  Box,
  Button,
  ChakraProvider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";

import React, { useCallback, useState } from "react";

import { useRouter } from "next/router";
import { Axios, customEvent } from "../../utils";
import { chakraTheme, colors } from "../../styles";
import { ColorIcon, HStack, VStack } from "../common";
import { EventDomainType } from "../../types";

export default function FileRequestModal({
  isOpen,
  onClose,
  accentColor,
}: {
  isOpen: boolean;
  onClose: () => void;
  path?: string | keyof typeof EventDomainType;
  accentColor?: string;
}) {
  const [value, setValue] = useState("");
  const router = useRouter();

  const handleClick = useCallback(() => {
    customEvent({
      domain: "common",
      path: "footer",
      trigger_and_target: "file_request_mdl-try_submit",
    });

    if (!value) {
      alert("번호를 입력해주세요");
      return;
    }
    if (!/^010-\d{4}-\d{4}$/.test(value)) {
      alert("번호 형식을 확인해주세요. (010-****-****)");
      return;
    }

    Axios.post("/checklist_request/", {
      telephone: value,
      path: router.asPath,
    })
      .then(() => {
        alert(
          "신청이 완료되었습니다. 입력해주신 전화번호로 곧 전달드리겠습니다.",
        );
        customEvent({
          domain: "common",
          path: "footer",
          trigger_and_target: "file_request_mdl-submit_success",
        });
        onClose();
      })
      .catch(() => {
        alert("번호를 확인해주세요.");
      });
  }, [value, router]);

  return (
    <ChakraProvider theme={chakraTheme}>
      <Box height="100%" width="100%">
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          closeOnOverlayClick={false}
          scrollBehavior="inside"
          isCentered={true}
          useInert={false}
        >
          <ModalOverlay width="100%" height="100%" />
          <ModalContent
            maxWidth="400px"
            mx="20px"
            borderRadius="16px"
            backgroundColor={colors.white}
          >
            <ModalBody padding="28px 24px 36px">
              <VStack width="100%">
                <HStack width="100%" justifyContent="end">
                  <div style={{ cursor: "pointer" }} onClick={onClose}>
                    <ColorIcon
                      name="close"
                      width={24}
                      height={24}
                      color={colors.black}
                    />
                  </div>
                </HStack>

                <VStack width="100%" gap={12}>
                  <div className="subtitle_21_b gray900">
                    장례 준비 체크리스트 받기
                  </div>
                  <Box
                    className="body_14_m"
                    style={{ color: colors.gray600, lineHeight: 1.65 }}
                  >
                    장례 절차부터 예상 비용까지, 지금 당장 필요한 정보만
                    담았습니다. 전화번호를 남겨주시면 카카오톡으로 바로
                    보내드립니다.
                  </Box>
                  <VStack
                    width="100%"
                    gap={6}
                    padding="8px 16px 24px 16px"
                    borderRadius="10px"
                    style={{ background: colors.gray50 }}
                  >
                    {[
                      "임종 후 단계별 장례 준비 절차",
                      "빈소 규모별 비용 및 예상 총액 비교",
                      "상조상품 연계 시 절감 가능 금액 안내",
                    ].map((text, i) => (
                      <HStack key={i} gap={8} alignItems="flex-start">
                        <Box
                          flexShrink={0}
                          width="5px"
                          height="5px"
                          borderRadius="50%"
                          marginTop="8px"
                          style={{
                            background: accentColor || colors.orange650,
                          }}
                        />
                        <Box
                          fontSize="13px"
                          style={{ color: colors.gray700, lineHeight: 1.6 }}
                        >
                          {text}
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>

                <VStack width="100%" gap={12}>
                  <Input
                    value={value}
                    onChange={(e) => {
                      const input = e.target.value.replace(/[^0-9]/g, "");
                      let formatted = input;
                      if (input.length <= 3) {
                        formatted = input;
                      } else if (input.length <= 7) {
                        formatted = `${input.slice(0, 3)}-${input.slice(3)}`;
                      } else if (input.length <= 11) {
                        formatted = `${input.slice(0, 3)}-${input.slice(3, 7)}-${input.slice(7)}`;
                      }
                      setValue(formatted);
                    }}
                    maxLength={13}
                    type="tel"
                    border={`1px solid ${colors.gray300}`}
                    _focus={{
                      borderColor: accentColor || colors.orange650,
                      boxShadow: "none",
                    }}
                    width="100%"
                    placeholder="010-****-****"
                    fontSize="16px"
                    padding="14px"
                    borderRadius="10px"
                  />

                  <HStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    padding="2px 0"
                  >
                    <HStack gap={6} alignItems="center">
                      <Box
                        fontSize="11px"
                        fontWeight={700}
                        style={{ color: colors.gray500 }}
                      >
                        필수
                      </Box>
                      <Box fontSize="13px" style={{ color: colors.gray700 }}>
                        개인정보 수집, 이용, 제공 동의
                      </Box>
                    </HStack>
                    <a
                      href="https://goifuneral.notion.site/e541b207305a46089ede35271b449b87"
                      target="_blank"
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: colors.gray600,
                        textDecoration: "none",
                      }}
                    >
                      보기
                    </a>
                  </HStack>

                  <Button
                    width="100%"
                    backgroundColor={accentColor || colors.orange650}
                    color={colors.white}
                    padding="16px"
                    borderRadius="12px"
                    fontSize="16px"
                    fontWeight={700}
                    onClick={handleClick}
                    _hover={{ opacity: 0.9 }}
                  >
                    동의하고 받아보기
                  </Button>
                </VStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </ChakraProvider>
  );
}
